'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface HeaderProps {
  isOpen?: boolean;
  isHidden?: boolean;
}

const Header = ({ isOpen = false, isHidden = false }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(isOpen);
  const [activeSection, setActiveSection] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  const navigationItems = [
    { label: 'Services', href: '#services', description: 'Industry-specific consulting' },
    { label: 'Industries', href: '#industries', description: 'Sector expertise' },
    { label: 'Results', href: '#solution', description: 'AI-Accelerated Intelligence' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = navigationItems.map(item => item.href.substring(1));
      const scrollPosition = window.scrollY + 150;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.substring(1);
    const element = document.getElementById(targetId);
    
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }

    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  if (isHidden) {
    return null;
  }

  return (
    <header data-component-id="src/components/common/Header.tsx:79:4" data-component-path="src/components/common/Header.tsx" data-component-line="79" data-component-end-line="152" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-250 ease-out ${
        isScrolled ? 'bg-background shadow-md' : 'bg-background'
      }`}
    >
      <div data-component-id="src/components/common/Header.tsx:84:6" data-component-path="src/components/common/Header.tsx" data-component-line="84" data-component-end-line="128" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20md%3Apx-6%20lg%3Apx-8%22%7D" className="container mx-auto px-4 md:px-6 lg:px-8">
        <div data-component-id="src/components/common/Header.tsx:85:8" data-component-path="src/components/common/Header.tsx" data-component-line="85" data-component-end-line="127" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20h-24%20gap-4%22%7D" className="flex items-center justify-between h-24 gap-4">
          <Link data-component-id="src/components/common/Header.tsx:86:10" data-component-path="src/components/common/Header.tsx" data-component-line="86" data-component-end-line="97" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Flanding-page%22%2C%22className%22%3A%22flex%20items-center%20flex-shrink-0%22%7D" href="/landing-page" className="flex items-center flex-shrink-0">
            <div data-component-id="src/components/common/Header.tsx:87:12" data-component-path="src/components/common/Header.tsx" data-component-line="87" data-component-end-line="96" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
              <AppImage data-component-id="src/components/common/Header.tsx:88:14" data-component-path="src/components/common/Header.tsx" data-component-line="88" data-component-end-line="95" data-component-file="Header.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22src%22%3A%22%2Fassets%2Fimages%2Fimage-1766311955269.png%22%2C%22alt%22%3A%22DataLitt%20Global%20Logo%22%2C%22className%22%3A%22h-20%20w-auto%20object-contain%20flex-shrink-0%22%7D"
                src="/assets/images/image-1766311955269.png"
                alt="DataLitt Global Logo"
                width={280}
                height={80}
                className="h-20 w-auto object-contain flex-shrink-0"
                priority
              />
            </div>
          </Link>

          <nav data-component-id="src/components/common/Header.tsx:99:10" data-component-path="src/components/common/Header.tsx" data-component-line="99" data-component-end-line="114" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20gap-2%20xl%3Agap-3%20flex-shrink%20min-w-0%20overflow-hidden%22%7D" className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink min-w-0 overflow-hidden">
            {navigationItems.map((item) => (
              <a data-component-id="src/components/common/Header.tsx:101:14" data-component-path="src/components/common/Header.tsx" data-component-line="101" data-component-end-line="112" data-component-file="Header.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%7D"
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`px-2 xl:px-3 py-2 rounded-md font-sans font-medium text-sm transition-all duration-250 ease-out whitespace-nowrap ${
                  activeSection === item.href.substring(1)
                    ? 'text-primary bg-card' :'text-text-secondary hover:text-primary hover:bg-card'
                }`}
                title={item.description}
              >
                {item.label}
              </a>
            ))}
          </nav>

          <button data-component-id="src/components/common/Header.tsx:116:10" data-component-path="src/components/common/Header.tsx" data-component-line="116" data-component-end-line="126" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22lg%3Ahidden%20p-2%20text-text-primary%20hover%3Atext-primary%20transition-colors%20duration-250%20flex-shrink-0%22%7D"
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-text-primary hover:text-primary transition-colors duration-250 flex-shrink-0"
            aria-label="Toggle mobile menu"
          >
            <Icon data-component-id="src/components/common/Header.tsx:121:12" data-component-path="src/components/common/Header.tsx" data-component-line="121" data-component-end-line="125" data-component-file="Header.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={28}
              variant="outline"
            />
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div data-component-id="src/components/common/Header.tsx:131:8" data-component-path="src/components/common/Header.tsx" data-component-line="131" data-component-end-line="150" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Ahidden%20bg-background%20border-t%20border-border%22%7D" className="lg:hidden bg-background border-t border-border">
          <nav data-component-id="src/components/common/Header.tsx:132:10" data-component-path="src/components/common/Header.tsx" data-component-line="132" data-component-end-line="149" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22container%20mx-auto%20px-5%20py-4%20space-y-1%22%7D" className="container mx-auto px-5 py-4 space-y-1">
            {navigationItems.map((item) => (
              <a data-component-id="src/components/common/Header.tsx:134:14" data-component-path="src/components/common/Header.tsx" data-component-line="134" data-component-end-line="147" data-component-file="Header.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%7D"
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`block px-4 py-3 rounded-md font-sans font-medium transition-all duration-250 ease-out ${
                  activeSection === item.href.substring(1)
                    ? 'text-primary bg-card' :'text-text-secondary hover:text-primary hover:bg-card'
                }`}
              >
                <div data-component-id="src/components/common/Header.tsx:143:16" data-component-path="src/components/common/Header.tsx" data-component-line="143" data-component-end-line="143" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22font-semibold%22%7D" className="font-semibold">{item.label}</div>
                <div data-component-id="src/components/common/Header.tsx:144:16" data-component-path="src/components/common/Header.tsx" data-component-line="144" data-component-end-line="146" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20mt-1%22%7D" className="text-xs text-muted-foreground mt-1">
                  {item.description}
                </div>
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;